import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  IconButton,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Button,
  CircularProgress,
  Chip,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Event as EventIcon,
  Security as SecurityIcon,
  LocalShipping as LocalShippingIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import useTenantNavigate from '../../hooks/useTenantNavigate';
import EmailDeliveryStatus from './EmailDeliveryStatus';
import {
  fetchNotificaciones,
  fetchNoLeidas,
  marcarComoLeida,
  marcarTodasLeidas,
  eliminarNotificacion,
  cerrarPanel,
} from '../../store/slices/notificacionesSlice';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

// Mapeo de módulos a iconos
const moduloIconos = {
  licencias: EventIcon,
  guardias: SecurityIcon,
  carros: LocalShippingIcon,
  material: LocalShippingIcon,
  citaciones: CampaignIcon,
  cuotas: AttachMoneyIcon,
  bomberos: PeopleIcon,
  sistema: InfoIcon,
};

// Mapeo de categorías a colores
const categoriaColores = {
  urgente: 'error',
  informativa: 'info',
  recordatorio: 'warning',
};

const NotificationCenter = () => {
  const dispatch = useDispatch();
  const navigate = useTenantNavigate();
  const theme = useTheme();
  
  const { notificaciones, noLeidas, loading } = useSelector(state => state.notificaciones);
  const { user } = useSelector(state => state.auth);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Cargar notificaciones al montar
  useEffect(() => {
    if (user) {
      dispatch(fetchNoLeidas());
    }
  }, [dispatch, user]);

  // Cargar notificaciones cuando se abre el panel
  useEffect(() => {
    if (open && user) {
      dispatch(fetchNotificaciones({ limite: 10 }));
    }
  }, [open, dispatch, user]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    dispatch(cerrarPanel());
  };

  const handleMarcarLeida = async (e, notificacionId) => {
    e.stopPropagation();
    await dispatch(marcarComoLeida(notificacionId));
  };

  const handleMarcarTodasLeidas = async () => {
    await dispatch(marcarTodasLeidas());
  };

  const handleEliminar = async (e, notificacionId) => {
    e.stopPropagation();
    await dispatch(eliminarNotificacion(notificacionId));
  };

  const handleClickNotificacion = (notificacion) => {
    // Marcar como leída
    if (!notificacion.leida) {
      dispatch(marcarComoLeida(notificacion.id));
    }
    
    // Navegar según el módulo
    const rutas = {
      licencias: '/licencias',
      guardias: '/guardias',
      carros: '/carros',
      material: '/material',
      citaciones: '/citaciones',
      cuotas: '/cuotas',
      bomberos: '/bomberos',
    };
    
    if (notificacion.moduloOrigen && rutas[notificacion.moduloOrigen]) {
      handleClose();
      navigate(rutas[notificacion.moduloOrigen]);
    }
  };

  const handleVerTodas = () => {
    handleClose();
    navigate('/notificaciones');
  };

  const handleConfigurar = () => {
    handleClose();
    navigate('/admin/notificaciones');
  };

  const getIconoModulo = (modulo) => {
    const IconComponent = moduloIconos[modulo] || InfoIcon;
    return <IconComponent />;
  };

  const formatearFecha = (fecha) => {
    try {
      return formatDistanceToNow(new Date(fecha), { 
        addSuffix: true, 
        locale: es 
      });
    } catch {
      return '';
    }
  };

  return (
    <>
      <Tooltip title="Notificaciones">
        <IconButton
          onClick={handleOpen}
          data-testid="notification-bell"
          aria-label="notificaciones"
          sx={{ 
            color: 'inherit',
            '&:hover': {
              backgroundColor: alpha(theme.palette.common.white, 0.1),
            }
          }}
        >
          <Badge 
            badgeContent={noLeidas} 
            color="error"
            max={99}
          >
            {noLeidas > 0 ? (
              <NotificationsActiveIcon sx={{ animation: noLeidas > 0 ? 'pulse 2s infinite' : 'none' }} />
            ) : (
              <NotificationsIcon />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 500,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
        }}>
          <Typography variant="h6" fontWeight="bold">
            Notificaciones
          </Typography>
          <Box>
            {noLeidas > 0 && (
              <Tooltip title="Marcar todas como leídas">
                <IconButton size="small" onClick={handleMarcarTodasLeidas}>
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Configurar">
              <IconButton size="small" onClick={handleConfigurar}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Lista de notificaciones */}
        <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={30} />
            </Box>
          ) : notificaciones.length === 0 ? (
            <Box sx={{ 
              p: 4, 
              textAlign: 'center',
              color: 'text.secondary'
            }}>
              <NotificationsIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography>No tienes notificaciones</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notificaciones.map((notificacion, index) => (
                <React.Fragment key={notificacion.id}>
                  <ListItem
                    button
                    onClick={() => handleClickNotificacion(notificacion)}
                    sx={{
                      bgcolor: notificacion.leida 
                        ? 'transparent' 
                        : alpha(theme.palette.primary.main, 0.08),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                      },
                      py: 1.5,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(
                            theme.palette[categoriaColores[notificacion.tipoNotificacion?.categoria] || 'info'].main,
                            0.15
                          ),
                          color: theme.palette[categoriaColores[notificacion.tipoNotificacion?.categoria] || 'info'].main,
                        }}
                      >
                        {getIconoModulo(notificacion.moduloOrigen)}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="body2" 
                            fontWeight={notificacion.leida ? 'normal' : 'bold'}
                            sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 200,
                            }}
                          >
                            {notificacion.titulo}
                          </Typography>
                          {notificacion.tipoNotificacion?.categoria === 'urgente' && (
                            <WarningIcon sx={{ fontSize: 14, color: 'error.main' }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {notificacion.mensaje}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.disabled">
                              {formatearFecha(notificacion.creadaEn)}
                            </Typography>
                            <EmailDeliveryStatus correo={notificacion.correo} />
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {!notificacion.leida && (
                          <Tooltip title="Marcar como leída">
                            <IconButton 
                              edge="end" 
                              size="small"
                              onClick={(e) => handleMarcarLeida(e, notificacion.id)}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Eliminar">
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={(e) => handleEliminar(e, notificacion.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < notificaciones.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ 
          p: 1.5, 
          borderTop: 1, 
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
        }}>
          <Button 
            fullWidth 
            size="small"
            onClick={handleVerTodas}
          >
            Ver todas las notificaciones
          </Button>
        </Box>
      </Popover>

      {/* CSS Animation */}
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </>
  );
};

export default NotificationCenter;
